<template>
  <div class="cart">
    <div class="container">
      <div class="cart__wrapper">
        <div class="cart__top">
          <h1 class="cart__title">{{ t('cart.title1') }} <span>{{ t('cart.title2') }}</span></h1>
        </div>
        <i18n-t class="cart__subtext" tag="p" scope="global" keypath="cart.text">
          <template #quantity>
            <span>{{ productsInCartQuantity }}</span>
          </template>
        </i18n-t>
        <div class="cart__main">
          <div class="cart__left">
            <product-cart-in-cart
                v-for="product in productsInCart"
                :key="product.node.product.uuid"
                :product="product.node.product"
            />
          </div>
          <div class="cart__right">
            <div class="cart__checkout">
              <div class="cart__checkout-top">
                <div>
                  <h2>{{ t('cart.total.title1') }} <span>{{ t('cart.total.title2') }}</span></h2>
                  <p>
                    <p style="color: #3b3b3b">€</p>
                    <span>{{ totalPrice }}</span>
                  </p>
                </div>
              </div>
              <el-select
                  v-model="currency"
                  size="large"
                  :placeholder="t('fields.currency')"
              >
                <el-option
                    v-for="item in currencyOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                />
              </el-select>
              <ui-checkbox
                  v-model="isAgree"
                  :id="1"
              >
                <i18n-t tag="p" scope="global" keypath="checkboxes.accept">
                  <template #terms>
                    <router-link
                        class="cart__link"
                        :to="translations.i18nRoute({ name: 'terms' })"
                    >
                      {{ t('docs.terms.title') }}
                    </router-link>
                  </template>
                  <template #policy>
                    <router-link
                        class="cart__link"
                        :to="translations.i18nRoute({ name: 'policy' })"
                    >
                      {{ t('docs.policy.title') }}
                    </router-link>
                  </template>
                </i18n-t>
              </ui-checkbox>
              <ui-button class="cart__checkout-button" :isDisabled="!isFormValid" @click="buyOrder">
                {{ t('buttons.checkout') }}
                <svg xmlns="http://www.w3.org/2000/svg" width="27" height="28" viewBox="0 0 27 28" fill="none">
                  <path d="M9.5625 6.125L17.4375 14L9.5625 21.875" stroke="#3b3b3b" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </ui-button>
            </div>
            <div class="cart__tag">
              <div class="cart__tag-top">
                <h2>{{ t('cart.tag.title1') }} <span>{{ t('cart.tag.title2') }}</span></h2>
              </div>
              <swiper
                  class="swiper"
                  :modules="[Navigation]"
                  :navigation="{ prevEl: '.cart__tag-button-prev', nextEl: '.cart__tag-button-next' }"
                  :loop="true"
                  :speed="500"
                  :spaceBetween="20"
                  :breakpoints="{
                    750: {
                      slidesPerView: 1
                    },
                    1100: {
                      slidesPerView: 2
                    }
                  }"
              >
                <swiper-slide
                    class="swiper__slide"
                    v-for="product in topProducts"
                    :key="product.node.uuid"
                >
                  <router-link :to="translations.i18nRoute({ name: 'product', params: { productSlug: product.node.slug } })">
                    <img :src="product.node.images.edges[0]?.node.image" :alt="product.node.name" >
                  </router-link>
                </swiper-slide>
              </swiper>
              <div class="cart__tag-buttons">
                <button class="cart__tag-button cart__tag-button-prev">
                  <
                </button>
                <button class="cart__tag-button cart__tag-button-next">
                  >
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {useI18n} from "vue-i18n";
import {computed, onMounted, ref} from "vue";
import {useCartStore} from "@/stores/cart.js";
import translations from "@/core/helpers/translations.js";
import ProductCartInCart from "@/components/cards/product-cart-in-cart.vue";
import UiButton from "@/components/ui/ui-button.vue";
import { Swiper, SwiperSlide } from 'swiper/vue';
import 'swiper/css/navigation';
import 'swiper/css';
import {Navigation} from "swiper/modules";
import {Products} from "@/core/api/products.js";
import {useRouter} from "vue-router";
import {Orders} from "@/core/api/orders.js";
import {ElNotification} from "element-plus";
import {useAuthStore} from "@/stores/auth.js";
import {Processes} from "@/core/api/processes.js";
import {Auth} from "@/core/api/auth.js";
import {isEmail} from "@/core/rules/textFieldRules.js";
import UiCheckbox from "@/components/ui/ui-checkbox.vue";

const {t} = useI18n()
const router = useRouter()
const cartStore = useCartStore()
const authStore = useAuthStore()

const accessToken = computed(() => {
  return authStore.accessToken ? authStore.accessToken : ''
})
const orderUuid = computed(() => {
  return cartStore.currentOrder ? cartStore.currentOrder.uuid : null
})
const userEmail = computed(() => {
  return authStore.user ? authStore.user.email : ''
})

const productsInCartQuantity = computed(() => {
  let count = 0
  cartStore.currentOrder.orderProducts?.edges.forEach((el) => {
    count = count + el.node.quantity
  })

  return count
})
const totalPrice = computed(() => {
  return cartStore.currentOrder?.totalPrice ? cartStore.currentOrder?.totalPrice : 0
})
const productsInCart = computed(() => {
  return cartStore.currentOrder.orderProducts ? cartStore.currentOrder.orderProducts.edges : []
})

const isAgree = ref(false)

const currency = ref('');
const currencyOptions = [
  {
    value: 'EUR',
    label: 'EUR',
  },
  {
    value: 'PLN',
    label: 'PLN',
  },
  {
    value: 'CHF',
    label: 'CHF',
  },
  {
    value: 'DKK',
    label: 'DKK',
  },
  {
    value: 'HUF',
    label: 'HUF',
  },
  {
    value: 'NOK',
    label: 'NOK',
  },
];

const isFormValid = computed(() => {
  return (
      totalPrice.value > 0 && currency.value && isAgree.value === true
  )
})

const paymentMin = ref('')
const paymentMax = ref('')

const topProducts = ref([])

const getProducts = async () => {
  const res = await Products.getProducts(`tags: "top", first: 6`)
  topProducts.value = res.data.products.edges
}

const buyOrder = async () => {
  const res = await Orders.buyOrder(accessToken.value, orderUuid.value, currency.value)

  if (res.errors) {
    ElNotification({
      title: t('popup.error'),
      message: res.errors[0].message,
      type: 'error'
    })
  } else {
    if (res?.data?.buyOrder?.transaction?.process?.url) {
      const currentOrder = await Orders.getOrders("PENDING", accessToken.value, userEmail.value)
      cartStore.setCurrentOrders(currentOrder.data.orders.edges[0].node)

      window.location.href = res.data.buyOrder.transaction.process.url
    } else {
      console.log(res.data)
    }
  }
}

onMounted( async () => {
  await getProducts()
  const res = await Processes.getPaymentLimits()
  paymentMin.value = res.data.paymentsLimits.minAmount
  paymentMax.value = res.data.paymentsLimits.maxAmount
})
</script>

<style lang="scss" scoped>
.cart {
  padding-top: 150px;

  &__wrapper {
    display: flex;
    flex-direction: column;
    gap: 35px;
  }

  &__link {
    text-decoration: underline;
  }

  &__top {
    border-bottom: 1px solid #1E1E1E;
  }

  &__title {
    width: fit-content;
    padding-right: 50px;
    padding-bottom: 10px;
    border-bottom: 3px solid #3b3b3b;
    color: #D9D9D9;
    font-size: 24px;
    font-weight: 700;
    line-height: 30px;

    & span {
      color: #3b3b3b;
    }
  }

  &__subtext {
    color: #000000;
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
  }

  &__main {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 75px;
  }

  &__left {
    display: flex;
    flex-direction: column;
    gap: 30px;
  }

  &__right {
    display: flex;
    flex-direction: column;
    gap: 50px;
  }

  &__checkout {
    display: flex;
    flex-direction: column;
    gap: 25px;

    &-top {
      border-bottom: 1px solid #1E1E1E;

      & div {
        width: fit-content;
        display: flex;
        align-items: center;
        gap: 50px;
        padding-bottom: 10px;
        border-bottom: 3px solid #3b3b3b;

        & h2 {
          color: #D9D9D9;
          font-size: 24px;
          font-weight: 700;
          line-height: 30px;

          & span {
            color: #3b3b3b;
          }
        }

        & p {
          display: flex;
          align-items: center;
          gap: 5px;

          & span {
            color: #000000;
            font-size: 24px;
            font-weight: 700;
            line-height: 30px;
          }
        }
      }
    }

    &-button {
      width: fit-content;
      padding: 10px 20px;
    }
  }

  &__tag {
    display: flex;
    flex-direction: column;
    gap: 25px;

    &-top {
      border-bottom: 1px solid #1E1E1E;

      & h2 {
        width: fit-content;
        padding-right: 50px;
        padding-bottom: 10px;
        border-bottom: 3px solid #3b3b3b;
        color: #D9D9D9;
        font-size: 24px;
        font-weight: 700;
        line-height: 30px;

        & span {
          color: #3b3b3b;
        }
      }
    }

    &-buttons {
      display: flex;
      align-items: center;
      gap: 15px;
    }

    &-button {
      cursor: pointer;
      border-radius: 3px;
      background-color: #FFFFFF;
      width: 65px;
      height: 30px;
      display: grid;
      place-items: center;
      transition: 0.2s;

      color: #3b3b3b;
      text-align: center;
      font-size: 22px;
      font-weight: 600;

      @include hover {
        transform: scale(1.5);
      }
    }
  }
}

.swiper {
  width: 620px;

  &__slide {
    & img {
      width: 300px;
      height: 225px;
      object-fit: cover;
      border-radius: 16px;
    }
  }
}

:deep(.el-select__wrapper) {
  box-shadow: 0 0 0 1px #1e1e1e inset !important;
}
:deep(.el-select__placeholder) {
  color: #1e1e1e !important;
  font-weight: 500 !important;
}
:deep(.el-select__placeholder.is-transparent) {
  color: #1e1e1e !important;
  font-weight: 500 !important;
}
:deep(.el-select__caret) {
  color: #1e1e1e !important;
}
:deep(.el-select-dropdown__item) {
  color: #1e1e1e !important;
}


@media (max-width: 1300px) {
  .cart {
    &__main {
      flex-direction: column;
      align-items: center;
      gap: 50px;
    }
  }
}

@media (max-width: 1100px) {
  .swiper__slide {
    & img{
      width: 300px;
      height: 225px;
    }
  }
}

@media (max-width: 750px) {
  .cart {
    &__left {
      width: 100%;
    }
  }

  .swiper {
    width: 300px;
  }
}
</style>